import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { settleSlip, slipPotential, type Slip, type SlipsMineResponse } from '@brochure/shared'
import { SideMark } from '@/components/stoccopong/SideMark'
import { slipFilterBucket } from '@/lib/slip'
import { formatOdds } from '@/lib/stockbet'
import { cn } from '@/lib/utils'
import { GateProgress, usePendingClock } from './LiveSlips'
import { useOutcomeLabel } from './useOutcomeLabel'

const priced = (slip: Slip) => ({
  type: slip.type, systemSizes: slip.systemSizes ?? undefined, stakePerColumn: slip.stakePerColumn, bonus: slip.bonus, maxPayout: slip.maxPayout,
  legs: slip.legs.map((l) => ({ banker: l.banker, selections: l.selections.map((s) => ({ odds: s.odds, status: s.status, deadHeat: s.deadHeat })) })),
})

/**
 * Le schedine dell'ospite (spec 4): bilancio, filtro aperte/concluse, biglietti con eventi, esiti e colonne vinte.
 * Le live in attesa stanno tra le aperte con la pista d'imbarco in piccolo; le annullate tra le concluse, senza vincita.
 */
export function MySlips({ data }: { data: SlipsMineResponse }) {
  const { t, i18n } = useTranslation()
  const label = useOutcomeLabel()
  const timing = usePendingClock(data)
  const hasOpen = data.slips.some((s) => slipFilterBucket(s.status) === 'open')
  // Il filtro segue le schedine (aperte se ce ne sono) finché l'ospite non ne sceglie uno: così una schedina appena giocata si vede.
  const [picked, setPicked] = useState<'open' | 'closed' | null>(null)
  const show = picked ?? (hasOpen ? 'open' : 'closed')
  const shown = data.slips.filter((s) => slipFilterBucket(s.status) === show)
  const footerResult = (slip: Slip) => {
    if (slip.status === 'won') return `+${slip.payout}`
    if (slip.status === 'lost') return t('stockbet.mine.status.lost')
    if (slip.status === 'rejected') return t('stockbet.pending.cancelled')
    // Singola e multipla hanno una colonna sola: la vincita è una. Il sistema dice sempre "min – max" (min: la colonna meno ricca), anche quando coincidono.
    const { min, max } = slipPotential(priced(slip))
    return slip.type === 'system' ? t('stockbet.mine.potentialRange', { min, max }) : t('stockbet.mine.potential', { count: max })
  }
  if (data.slips.length === 0) return <p className="font-body text-paper/60">{t('stockbet.mine.empty')}</p>
  return (
    <div className="space-y-4">
      <dl data-testid="slips-summary" className="grid grid-cols-3 gap-2 rounded-xl border border-paper/12 bg-paper/[0.04] p-3 text-center font-body">
        {(['staked', 'won', 'net'] as const).map((key) => (
          <div key={key}>
            <dt className="text-xs text-paper/55">{t(`stockbet.mine.summary.${key}`)}</dt>
            <dd className={cn('font-display text-xl tabular-nums', key === 'net' && data.summary.net > 0 && 'text-sun')}>
              {key === 'net' && data.summary.net > 0 ? '+' : ''}
              {data.summary[key]}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex gap-2">
        {(['open', 'closed'] as const).map((key) => (
          <button key={key} type="button" aria-pressed={show === key} data-testid={`slips-filter-${key}`} onClick={() => setPicked(key)} className={cn('min-h-11 rounded-full border px-4 font-body text-sm font-semibold', show === key ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper')}>
            {t(`stockbet.mine.filters.${key}`)}
          </button>
        ))}
      </div>
      {shown.length === 0 && <p data-testid="slips-filter-empty" className="font-body text-sm text-paper/60">{t(show === 'open' ? 'stockbet.mine.emptyOpen' : 'stockbet.mine.emptyClosed')}</p>}
      <ul className="grid grid-cols-1 gap-2.5 lg:grid-cols-2" data-testid="my-slips">
        {shown.map((slip) => {
          const result = settleSlip(priced(slip))
          return (
            <li key={slip.id} data-testid={`slip-${slip.id}`} className="overflow-hidden rounded-xl border border-paper/12 bg-paper/[0.04]">
              <p className="flex items-center justify-between gap-2 border-b border-paper/10 px-3 py-2 font-body text-xs font-semibold text-paper/70">
                <span>{t(`stockbet.mine.type.${slip.type}`, { sizes: (slip.systemSizes ?? []).join('-'), legs: slip.legs.filter((l) => !l.banker).length, count: slip.columns })}</span>
                <span className="tabular-nums">{new Date(slip.placedAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</span>
              </p>
              {slip.status === 'pending' && (
                <div data-testid={`slip-pending-${slip.id}`} className="border-b border-paper/10 px-3 py-2">
                  <p className="font-body text-xs font-semibold text-sun">{t('stockbet.pending.waiting')}</p>
                  <GateProgress {...timing(slip)} className="mt-1.5" />
                </div>
              )}
              <ul className="space-y-2 px-3 py-2.5">
                {slip.legs.map((leg) => {
                  // Antepost: niente partita, i nomi delle squadre e dei giocatori scelti li porta l'evento.
                  const names = Object.fromEntries((leg.match?.players ?? leg.subjects ?? []).map((p) => [p.id, p.name]))
                  return (
                    <li key={leg.id} className="font-body text-sm text-paper">
                      <p className="flex items-center gap-1.5 text-xs text-paper/55">
                        {leg.match ? (
                          <>
                            <SideMark side="home" className="h-2.5" />
                            <span className="min-w-0 flex-1 truncate">{`${leg.match.homeTeam.name} – ${leg.match.awayTeam.name}`}</span>
                            <SideMark side="away" className="h-2.5" />
                          </>
                        ) : (
                          <span className="min-w-0 flex-1 truncate">{t('stockbet.outrights.legTitle')}</span>
                        )}
                        {leg.banker && <span className="rounded bg-sun px-1 text-[0.6rem] font-bold text-ink">F</span>}
                      </p>
                      {leg.selections.map((s) => (
                        <p key={s.id} className="mt-0.5 flex items-center gap-2">
                          <span className={cn('min-w-0 flex-1 truncate', s.status === 'lost' && 'line-through opacity-55')}>
                            {t(`stockbet.marketNames.${s.market}`)}: <span className="font-semibold">{label({ market: s.market, selection: s.selection, ...(s.line !== null && { line: s.line }), ...(s.subjectId !== null && { subjectId: s.subjectId }) }, names)}</span>
                          </span>
                          <span className="shrink-0 tabular-nums text-paper/80">{formatOdds(s.odds, i18n.language)}</span>
                          {s.status === 'won' && <Check className="size-4 shrink-0 text-sun" role="img" aria-label={t('stockbet.resolution.won')} />}
                          {s.status === 'lost' && <X className="size-4 shrink-0 text-paper/55" role="img" aria-label={t('stockbet.resolution.lost')} />}
                          {s.status === 'void' && <span className="shrink-0 text-xs text-paper/60">{t('stockbet.resolution.void')}</span>}
                          {s.status === 'open' && <span className="size-2 shrink-0 rounded-full bg-paper/40" role="img" aria-label={t('stockbet.mine.status.open')} />}
                        </p>
                      ))}
                    </li>
                  )
                })}
              </ul>
              <p className="flex items-center justify-between gap-2 border-t-2 border-dashed border-paper/15 px-3 py-2 font-body text-sm">
                <span className="shrink-0 text-paper/70 tabular-nums">
                  {t('stockbet.mine.stake', { count: slip.totalStake })}
                  {/* "0/2 colonne" su una schedina ancora in gioco sembrerebbe persa: le colonne vinte si contano da quando ce n'è una o a schedina chiusa. */}
                  {slip.type === 'system' && (slip.status === 'won' || slip.status === 'lost' || result.columns.won > 0) && ` · ${t('stockbet.mine.columnsWon', { won: result.columns.won, total: result.columns.total })}`}
                </span>
                <span className={cn('min-w-0 truncate text-right font-semibold tabular-nums', slip.status === 'won' ? 'text-sun' : slip.status === 'lost' || slip.status === 'rejected' ? 'text-paper/50' : 'text-paper')}>
                  {footerResult(slip)}
                </span>
              </p>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
